import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import {
  CheckCircle2, Users, Briefcase, Download, Send,
} from "lucide-react";
import { salaryApi, emailApi } from "../../lib/api";

export const LonespecTab = ({ allSpecs, specPeriod, setSpecPeriod, loadAllSpecs }) => {
  if (!allSpecs) return null;

  const periodOptions = (() => {
    const opts = [];
    const now = new Date();
    for (let i = -3; i <= 1; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const prevM = new Date(d.getFullYear(), d.getMonth() - 1, 25);
      const label = `${prevM.getDate()}/${prevM.getMonth()+1} - 25/${d.getMonth()+1} ${d.getFullYear()}`;
      opts.push({ val, label });
    }
    return opts;
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={specPeriod} onValueChange={(v) => { setSpecPeriod(v); loadAllSpecs(v); }}>
          <SelectTrigger className="w-[250px] bg-[#080c12] border-white/[0.06] text-white text-sm rounded-xl">
            <SelectValue placeholder="Valj period" />
          </SelectTrigger>
          <SelectContent className="bg-[#0d1218] border-white/[0.06]">
            {periodOptions.map(o => (
              <SelectItem key={o.val} value={o.val} className="text-white">{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-white/25">{allSpecs.period_label}</span>
      </div>

      {allSpecs.specs?.filter(s => !s.is_ue).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#6dbf4a]" /> Anstallda
          </h3>
          <div className="space-y-2">
            {allSpecs.specs.filter(s => !s.is_ue).map(s => (
              <div key={s.user_id} className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#6dbf4a]/15 flex items-center justify-center text-xs text-[#6dbf4a] font-bold">{s.user_name.charAt(0)}</div>
                  <div>
                    <p className="text-sm font-medium text-white">{s.user_name}</p>
                    <p className="text-xs text-white/25">{s.total_hours.toFixed(1)}h -- Brutto {s.gross?.toLocaleString("sv-SE")} kr</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="stat-number text-sm font-bold text-white">{s.net?.toLocaleString("sv-SE")} kr</span>
                  {s.approved ? (
                    <span className="text-[10px] px-2 py-1 rounded-lg bg-emerald-500/15 text-emerald-400 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Godkand</span>
                  ) : (
                    <Button size="sm" onClick={async () => {
                      try {
                        await salaryApi.approve(s.user_id, allSpecs.period);
                        loadAllSpecs(specPeriod);
                        toast.success(`${s.user_name} godkand`);
                      } catch { toast.error("Kunde inte godkanna"); }
                    }} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700">
                      Godkann
                    </Button>
                  )}
                  <a href={`${salaryApi.getPdfUrl(s.user_id, allSpecs.period)}&token=${localStorage.getItem('token')}`}
                    target="_blank" rel="noopener noreferrer" className="text-[#6dbf4a] hover:text-[#5da83d]">
                    <Download className="w-4 h-4" />
                  </a>
                  <button onClick={async () => {
                    const email = prompt(`Skicka lonespec till:`, '');
                    if (!email) return;
                    try {
                      await emailApi.sendSpec({ recipient_email: email, subject: `Lonespec ${allSpecs.period_label} - ${s.user_name}`, user_id: s.user_id, period: allSpecs.period, spec_type: 'salary' });
                      toast.success(`Skickat till ${email}`);
                    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte skicka"); }
                  }} className="text-blue-400 hover:text-blue-300" title="Skicka via e-post">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {allSpecs.specs?.filter(s => s.is_ue).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-white mb-3 mt-4 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-blue-400" /> Underentreprenorer
          </h3>
          <div className="space-y-2">
            {allSpecs.specs.filter(s => s.is_ue).map(s => (
              <div key={s.user_id} className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-blue-500/15 flex items-center justify-center text-xs text-blue-400 font-bold">{s.user_name.charAt(0)}</div>
                  <div>
                    <p className="text-sm font-medium text-white">{s.user_name}</p>
                    <p className="text-xs text-white/25">{s.total_hours.toFixed(1)}h -- {s.hourly_rate} kr/h</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="stat-number text-sm font-bold text-white">{s.grand_total?.toLocaleString("sv-SE")} kr</span>
                  {s.approved ? (
                    <span className="text-[10px] px-2 py-1 rounded-lg bg-emerald-500/15 text-emerald-400 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Godkand</span>
                  ) : (
                    <Button size="sm" onClick={async () => {
                      try {
                        await salaryApi.approve(s.user_id, allSpecs.period);
                        loadAllSpecs(specPeriod);
                        toast.success(`${s.user_name} godkand`);
                      } catch { toast.error("Kunde inte godkanna"); }
                    }} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700">
                      Godkann
                    </Button>
                  )}
                  <a href={`${salaryApi.getPdfUrl(s.user_id, allSpecs.period)}&token=${localStorage.getItem('token')}`}
                    target="_blank" rel="noopener noreferrer" className="text-[#6dbf4a] hover:text-[#5da83d]">
                    <Download className="w-4 h-4" />
                  </a>
                  <button onClick={async () => {
                    const email = prompt(`Skicka fakturaunderlag till:`, '');
                    if (!email) return;
                    try {
                      await emailApi.sendSpec({ recipient_email: email, subject: `Fakturaunderlag ${allSpecs.period_label} - ${s.user_name}`, user_id: s.user_id, period: allSpecs.period, spec_type: 'ue' });
                      toast.success(`Skickat till ${email}`);
                    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte skicka"); }
                  }} className="text-blue-400 hover:text-blue-300" title="Skicka via e-post">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
